import { useEffect, useRef, useState } from "react";
import axios from "../axios";

export default function MetaInbox() {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [filter, setFilter] = useState("all");
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

  const messagesEndRef = useRef(null);

  const loadChats = async () => {
    try {
      const res = await axios.get("/meta-inbox/chats");
      setChats(res.data || []);
    } catch (err) {
      console.error("Load social chats error:", err.response?.data || err.message);
    }
  };

  const loadMessages = async (chat) => {
    try {
      setSelectedChat(chat);
      const res = await axios.get(
        `/meta-inbox/chat/${chat.platform}/${chat.conversation_type}/${chat.sender_id}`
      );
      setMessages(res.data || []);
      await axios.post("/meta-inbox/mark-read", {
        platform: chat.platform,
        conversation_type: chat.conversation_type,
        sender_id: chat.sender_id,
      });
      loadChats();
    } catch (err) {
      console.error("Load social messages error:", err.response?.data || err.message);
    }
  };

  const sendReply = async () => {
    if (!selectedChat || !reply.trim()) return;
    try {
      if (selectedChat.conversation_type === "comment") {
        const lastIncomingComment = [...messages]
          .reverse()
          .find((m) => m.direction === "incoming" && m.comment_id);
        await axios.post("/meta-inbox/comment-reply", {
          platform: selectedChat.platform,
          comment_id: lastIncomingComment?.comment_id || selectedChat.sender_id,
          message: reply,
        });
      } else {
        await axios.post("/meta-inbox/send", {
          platform: selectedChat.platform,
          sender_id: selectedChat.sender_id,
          message: reply,
        });
      }
      setReply("");
      loadMessages(selectedChat);
      loadChats();
    } catch (err) {
      console.error("Send social reply error:", err.response?.data || err.message);
      alert(err.response?.data?.msg || "Reply failed ❌");
    }
  };

useEffect(() => {
  loadChats();

  const timer = setInterval(() => {
    loadChats();

    // ✅ Sirf tab auto refresh messages karo
    // jab user bottom par ho
    if (selectedChat && shouldAutoScroll) {
      loadMessages(selectedChat);
    }
  }, 5000);

  return () => clearInterval(timer);

  // eslint-disable-next-line
}, [selectedChat, shouldAutoScroll]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const filteredChats = chats.filter((chat) => {
    if (filter === "all") return true;
    if (filter === "instagram") return chat.platform === "instagram";
    if (filter === "facebook") return chat.platform === "facebook";
    if (filter === "dm") return chat.conversation_type === "dm";
    if (filter === "comment") return chat.conversation_type === "comment";
    if (filter === "booked") return String(chat.lead_status || "") === "Booked";
    if (filter === "followup") return !!chat.followup_at;
    return true;
  });

  const filterOptions = [
    { value: "all", label: "All", icon: "⊞" },
    { value: "instagram", label: "Instagram", icon: "◈" },
    { value: "facebook", label: "Facebook", icon: "◉" },
    { value: "dm", label: "DMs", icon: "✉" },
    { value: "comment", label: "Comments", icon: "◎" },
    { value: "booked", label: "Booked", icon: "✓" },
    { value: "followup", label: "Follow-ups", icon: "⏱" },
  ];

  const getAvatar = (chat) => {
    const name = chat.customer_name || chat.sender_name || chat.sender_id || "?";
    return name.charAt(0).toUpperCase();
  };

  const getAvatarColor = (chat) => {
    if (chat.platform === "instagram") return "from-pink-500 to-purple-600";
    return "from-blue-500 to-blue-700";
  };

  const formatTime = (t) => {
    if (!t) return "";
    return String(t).slice(0, 16).replace("T", " ");
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

        .meta-inbox * {
          font-family: 'Sora', sans-serif;
          box-sizing: border-box;
        }

        .meta-inbox {
          background: #f0f2f8;
          min-height: 100vh;
          padding: 28px 32px;
        }

        /* Header */
        .inbox-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .inbox-title-wrap {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .inbox-icon {
          width: 46px;
          height: 46px;
          border-radius: 14px;
          background: linear-gradient(135deg, #2563eb, #7c3aed);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          box-shadow: 0 4px 14px rgba(37,99,235,0.35);
        }

        .inbox-title {
          font-size: 22px;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.5px;
        }

        .inbox-subtitle {
          font-size: 12px;
          color: #94a3b8;
          font-weight: 400;
          margin-top: 1px;
        }

        /* Live badge */
        .live-badge {
          display: flex;
          align-items: center;
          gap: 7px;
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 20px;
          padding: 6px 14px;
          font-size: 12px;
          font-weight: 600;
          color: #22c55e;
        }

        .live-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #22c55e;
          animation: pulse-green 1.5s infinite;
        }

        @keyframes pulse-green {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }

        /* Filter pills */
        .filter-row {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }

        .filter-pill {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 7px 14px;
          border-radius: 20px;
          border: 1.5px solid #e2e8f0;
          background: #fff;
          font-size: 12px;
          font-weight: 500;
          color: #64748b;
          cursor: pointer;
          transition: all 0.18s ease;
          white-space: nowrap;
        }

        .filter-pill:hover {
          border-color: #2563eb;
          color: #2563eb;
          background: #eff6ff;
          transform: translateY(-1px);
        }

        .filter-pill.active {
          background: linear-gradient(135deg, #2563eb, #7c3aed);
          border-color: transparent;
          color: #fff;
          box-shadow: 0 3px 10px rgba(37,99,235,0.3);
        }

        .filter-pill .pill-icon {
          font-size: 13px;
        }

        /* Grid */
        .inbox-grid {
          display: grid;
          grid-template-columns: 340px 1fr;
          gap: 16px;
          height: calc(100vh - 200px);
          min-height: 500px;
        }

        /* Chat List Panel */
        .chat-list-panel {
          background: #fff;
          border-radius: 20px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 2px 16px rgba(15,23,42,0.07);
          border: 1px solid #e8ecf4;
        }

        .panel-header {
          padding: 16px 18px;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .panel-header-title {
          font-size: 13px;
          font-weight: 600;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: 0.6px;
        }

        .chat-count-badge {
          background: #f1f5f9;
          color: #64748b;
          font-size: 11px;
          font-weight: 600;
          padding: 3px 9px;
          border-radius: 10px;
        }

        .chat-list-scroll {
          flex: 1;
          overflow-y: auto;
        }

        .chat-list-scroll::-webkit-scrollbar {
          width: 4px;
        }

        .chat-list-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .chat-list-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }

        .chat-empty {
          padding: 48px 24px;
          text-align: center;
          color: #94a3b8;
          font-size: 14px;
        }

        .chat-empty-icon {
          font-size: 36px;
          margin-bottom: 10px;
          opacity: 0.4;
        }

        /* Chat Item */
        .chat-item {
          padding: 14px 18px;
          border-bottom: 1px solid #f8fafc;
          cursor: pointer;
          transition: all 0.15s ease;
          display: flex;
          gap: 12px;
          align-items: flex-start;
          position: relative;
        }

        .chat-item:hover {
          background: #f8fafc;
        }

        .chat-item.selected {
          background: linear-gradient(135deg, #eff6ff, #f5f3ff);
          border-left: 3px solid #2563eb;
        }

        .chat-item.unread {
          background: #fafbff;
        }

        .chat-avatar {
          width: 42px;
          height: 42px;
          border-radius: 13px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          font-weight: 700;
          color: #fff;
          flex-shrink: 0;
          position: relative;
        }

        .platform-dot {
          position: absolute;
          bottom: -2px;
          right: -2px;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          border: 2px solid #fff;
          font-size: 7px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .platform-dot.instagram { background: linear-gradient(135deg, #e1306c, #833ab4); }
        .platform-dot.facebook { background: #1877f2; }

        .chat-info {
          flex: 1;
          min-width: 0;
        }

        .chat-name-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;
        }

        .chat-name {
          font-size: 13.5px;
          font-weight: 600;
          color: #0f172a;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 160px;
        }

        .chat-time {
          font-size: 10.5px;
          color: #94a3b8;
          white-space: nowrap;
          font-family: 'JetBrains Mono', monospace;
        }

        .chat-tags {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
          margin-bottom: 5px;
        }

        .tag {
          font-size: 10px;
          font-weight: 600;
          padding: 2px 7px;
          border-radius: 6px;
          letter-spacing: 0.2px;
        }

        .tag-instagram { background: #fdf2f8; color: #be185d; border: 1px solid #fbcfe8; }
        .tag-facebook { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
        .tag-dm { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
        .tag-comment { background: #fefce8; color: #a16207; border: 1px solid #fde68a; }
        .tag-status { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
        .tag-service { background: #faf5ff; color: #7e22ce; border: 1px solid #e9d5ff; }

        .chat-last-msg {
          font-size: 12px;
          color: #64748b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .unread-badge {
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: #fff;
          font-size: 10px;
          font-weight: 700;
          min-width: 18px;
          height: 18px;
          border-radius: 9px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 5px;
          box-shadow: 0 2px 6px rgba(239,68,68,0.4);
        }

        .followup-info {
          font-size: 10.5px;
          color: #ea580c;
          font-weight: 600;
          margin-top: 3px;
        }

        .appointment-info {
          font-size: 10.5px;
          color: #059669;
          font-weight: 600;
          margin-top: 2px;
        }

        /* Chat Window */
        .chat-window {
          background: #fff;
          border-radius: 20px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 2px 16px rgba(15,23,42,0.07);
          border: 1px solid #e8ecf4;
        }

        .chat-window-header {
          padding: 16px 22px;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #fff;
        }

        .chat-window-name {
          font-size: 16px;
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.3px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .chat-window-sub {
          font-size: 12px;
          color: #94a3b8;
          margin-top: 1px;
        }

        .header-tags {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        /* Meta info bar */
        .meta-bar {
          padding: 10px 22px;
          background: #f8fafc;
          border-bottom: 1px solid #f1f5f9;
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }

        .meta-bar-item {
          font-size: 12px;
          color: #475569;
        }

        .meta-bar-item b {
          color: #1e293b;
          font-weight: 600;
        }

        /* Messages */
        .messages-area {
          flex: 1;
          overflow-y: auto;
          padding: 22px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          background: #f8fafc;
        }

        .messages-area::-webkit-scrollbar { width: 4px; }
        .messages-area::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

        .msg-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #94a3b8;
          gap: 12px;
        }

        .msg-empty-icon {
          font-size: 48px;
          opacity: 0.3;
        }

        .msg-bubble-wrap {
          display: flex;
          flex-direction: column;
        }

        .msg-bubble-wrap.outgoing {
          align-items: flex-end;
        }

        .msg-bubble {
          max-width: 72%;
          padding: 11px 15px;
          border-radius: 16px;
          font-size: 13.5px;
          line-height: 1.5;
          color: #0f172a;
        }

        .msg-bubble.incoming {
          background: #fff;
          border-bottom-left-radius: 4px;
          box-shadow: 0 1px 6px rgba(15,23,42,0.07);
        }

        .msg-bubble.outgoing {
          background: linear-gradient(135deg, #2563eb, #3b82f6);
          color: #fff;
          border-bottom-right-radius: 4px;
          box-shadow: 0 2px 10px rgba(37,99,235,0.25);
        }

        .msg-meta {
          font-size: 10.5px;
          color: #94a3b8;
          margin-top: 4px;
          display: flex;
          gap: 6px;
          align-items: center;
          padding: 0 4px;
          font-family: 'JetBrains Mono', monospace;
        }

        .msg-meta .bot-tag {
          background: #faf5ff;
          color: #7c3aed;
          padding: 1px 6px;
          border-radius: 4px;
          font-size: 9.5px;
          font-weight: 600;
        }

        .msg-meta .comment-tag {
          background: #fefce8;
          color: #a16207;
          padding: 1px 6px;
          border-radius: 4px;
          font-size: 9.5px;
          font-weight: 600;
        }

        /* Reply box */
        .reply-box {
          padding: 14px 18px;
          border-top: 1px solid #f1f5f9;
          background: #fff;
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .reply-input {
          flex: 1;
          border: 1.5px solid #e2e8f0;
          border-radius: 12px;
          padding: 11px 16px;
          font-size: 13.5px;
          font-family: 'Sora', sans-serif;
          color: #0f172a;
          outline: none;
          transition: border-color 0.2s;
          background: #f8fafc;
        }

        .reply-input:focus {
          border-color: #2563eb;
          background: #fff;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
        }

        .reply-input::placeholder {
          color: #94a3b8;
        }

        .send-btn {
          background: linear-gradient(135deg, #2563eb, #7c3aed);
          color: #fff;
          border: none;
          border-radius: 12px;
          padding: 11px 22px;
          font-size: 13.5px;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Sora', sans-serif;
          transition: all 0.18s ease;
          box-shadow: 0 3px 12px rgba(37,99,235,0.3);
          display: flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
        }

        .send-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 5px 16px rgba(37,99,235,0.4);
        }

        .send-btn:active {
          transform: translateY(0);
        }

        /* Empty state for no chat selected */
        .no-chat-selected {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          gap: 14px;
          color: #94a3b8;
        }

        .no-chat-icon {
          width: 72px;
          height: 72px;
          border-radius: 22px;
          background: linear-gradient(135deg, #eff6ff, #f5f3ff);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 32px;
          border: 1px solid #e2e8f0;
        }

        .no-chat-title {
          font-size: 15px;
          font-weight: 600;
          color: #475569;
        }

        .no-chat-sub {
          font-size: 12.5px;
          color: #94a3b8;
        }

        /* Mobile back button (hidden on desktop) */
        .back-btn {
          display: none;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          border-radius: 10px;
          border: 1.5px solid #e2e8f0;
          background: #fff;
          color: #475569;
          font-size: 16px;
          line-height: 1;
          cursor: pointer;
          flex-shrink: 0;
          padding: 0;
        }

        .back-btn:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
        }

        /* ===== Tablet ===== */
        @media (max-width: 1024px) {
          .inbox-grid {
            grid-template-columns: 280px 1fr;
          }
        }

        /* ===== Mobile: stack into single-pane, master/detail navigation ===== */
        @media (max-width: 768px) {
          .meta-inbox {
            padding: 14px;
            min-height: 100dvh;
          }

          .inbox-header {
            margin-bottom: 14px;
          }

          .inbox-title-wrap {
            gap: 10px;
          }

          .inbox-icon {
            width: 38px;
            height: 38px;
            font-size: 16px;
            border-radius: 11px;
          }

          .inbox-title {
            font-size: 16.5px;
          }

          .inbox-subtitle {
            font-size: 10.5px;
          }

          .live-badge {
            padding: 5px 10px;
            font-size: 11px;
            flex-shrink: 0;
          }

          .filter-row {
            flex-wrap: nowrap;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            margin: 0 -14px 14px;
            padding: 0 14px 4px;
          }

          .filter-row::-webkit-scrollbar {
            display: none;
          }

          .filter-pill {
            flex-shrink: 0;
          }

          /* Single column, full-height master/detail pane */
          .inbox-grid {
            grid-template-columns: 1fr;
            height: calc(100vh - 148px);
            height: calc(100dvh - 148px);
            min-height: 420px;
          }

          .chat-list-panel,
          .chat-window {
            border-radius: 16px;
          }

          /* Default: list visible, detail hidden */
          .chat-window {
            display: none;
          }

          /* Chat selected: detail visible, list hidden */
          .inbox-grid.show-chat .chat-list-panel {
            display: none;
          }

          .inbox-grid.show-chat .chat-window {
            display: flex;
          }

          .back-btn {
            display: flex;
          }

          .panel-header {
            padding: 14px 16px;
          }

          .chat-item {
            padding: 12px 16px;
          }

          .chat-name {
            max-width: 130px;
          }

          .chat-window-header {
            padding: 12px 14px;
            flex-wrap: wrap;
            row-gap: 8px;
          }

          .header-tags {
            flex-shrink: 0;
          }

          .meta-bar {
            padding: 8px 14px;
            gap: 12px;
          }

          .messages-area {
            padding: 14px;
          }

          .msg-bubble {
            max-width: 86%;
          }

          .reply-box {
            padding: 10px 12px;
          }

          .send-btn {
            padding: 11px 16px;
          }
        }

        /* ===== Small phones: trim further ===== */
        @media (max-width: 420px) {
          .inbox-subtitle {
            display: none;
          }

          .live-badge {
            padding: 5px 9px;
          }

          .filter-pill {
            padding: 6px 11px;
            font-size: 11px;
          }

          .chat-name {
            max-width: 95px;
          }

          .chat-window-sub {
            display: none;
          }

          .send-btn .send-label {
            display: none;
          }

          .send-btn {
            padding: 11px 14px;
          }
        }
      `}</style>

      <div className="meta-inbox">
        {/* Header */}
        <div className="inbox-header">
          <div className="inbox-title-wrap">
            <div className="inbox-icon">💬</div>
            <div>
              <div className="inbox-title">Meta Social Inbox</div>
              <div className="inbox-subtitle">Instagram & Facebook unified</div>
            </div>
          </div>
          <div className="live-badge">
            <div className="live-dot" />
            Live
          </div>
        </div>

        {/* Filter Pills */}
        <div className="filter-row">
          {filterOptions.map((opt) => (
            <button
              key={opt.value}
              className={`filter-pill ${filter === opt.value ? "active" : ""}`}
              onClick={() => setFilter(opt.value)}
            >
              <span className="pill-icon">{opt.icon}</span>
              {opt.label}
            </button>
          ))}
        </div>

        {/* Main Grid */}
        <div className={`inbox-grid ${selectedChat ? "show-chat" : ""}`}>
          {/* Left: Chat List */}
          <div className="chat-list-panel">
            <div className="panel-header">
              <div className="panel-header-title">Conversations</div>
              <div className="chat-count-badge">{filteredChats.length}</div>
            </div>
            <div className="chat-list-scroll">
              {filteredChats.length === 0 && (
                <div className="chat-empty">
                  <div className="chat-empty-icon">📭</div>
                  <div>No conversations found</div>
                </div>
              )}

              {filteredChats.map((chat, index) => {
                const isSelected =
                  selectedChat?.sender_id === chat.sender_id &&
                  selectedChat?.platform === chat.platform &&
                  selectedChat?.conversation_type === chat.conversation_type;
                const hasUnread = Number(chat.unread_count || 0) > 0;

                return (
                  <div
                    key={`${chat.platform}-${chat.conversation_type}-${chat.sender_id}-${index}`}
                    className={`chat-item ${isSelected ? "selected" : ""} ${hasUnread ? "unread" : ""}`}
                    onClick={() => loadMessages(chat)}
                  >
                    {/* Avatar */}
                    <div
                      className={`chat-avatar bg-gradient-to-br ${getAvatarColor(chat)}`}
                    >
                      {getAvatar(chat)}
                      <div className={`platform-dot ${chat.platform}`}>
                        {chat.platform === "instagram" ? "IG" : "FB"}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="chat-info">
                      <div className="chat-name-row">
                        <div className="chat-name">
                          {chat.customer_name || chat.sender_name || chat.sender_id}
                        </div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          {hasUnread && (
                            <div className="unread-badge">{chat.unread_count}</div>
                          )}
                        </div>
                      </div>

                      <div className="chat-tags">
                        <span
                          className={`tag ${
                            chat.platform === "instagram" ? "tag-instagram" : "tag-facebook"
                          }`}
                        >
                          {chat.platform}
                        </span>
                        <span
                          className={`tag ${
                            chat.conversation_type === "dm" ? "tag-dm" : "tag-comment"
                          }`}
                        >
                          {chat.conversation_type}
                        </span>
                        {chat.lead_status && (
                          <span className="tag tag-status">{chat.lead_status}</span>
                        )}
                        {chat.service_interest && (
                          <span className="tag tag-service">{chat.service_interest}</span>
                        )}
                      </div>

                      {chat.followup_at && (
                        <div className="followup-info">⏱ {formatTime(chat.followup_at)}</div>
                      )}
                      {(chat.appointment_date || chat.appointment_time) && (
                        <div className="appointment-info">
                          ✓ {chat.appointment_date || ""} {chat.appointment_time || ""}
                        </div>
                      )}

                      <div className="chat-last-msg">{chat.last_message || ""}</div>

                      <div className="chat-time" style={{ marginTop: 4 }}>
                        {formatTime(chat.last_time)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Chat Window */}
          <div className="chat-window">
            {!selectedChat ? (
              <div className="no-chat-selected">
                <div className="no-chat-icon">💬</div>
                <div className="no-chat-title">Select a conversation</div>
                <div className="no-chat-sub">
                  Choose from Instagram or Facebook chats
                </div>
              </div>
            ) : (
              <>
                {/* Window Header */}
                <div className="chat-window-header">
                  <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
                    <button
                      type="button"
                      className="back-btn"
                      onClick={() => setSelectedChat(null)}
                      aria-label="Back to conversations"
                    >
                      ←
                    </button>
                    <div
                      className={`chat-avatar bg-gradient-to-br ${getAvatarColor(selectedChat)}`}
                      style={{ width: 40, height: 40, borderRadius: 12, fontSize: 15 }}
                    >
                      {getAvatar(selectedChat)}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="chat-window-name">
                        {selectedChat.customer_name ||
                          selectedChat.sender_name ||
                          selectedChat.sender_id}
                      </div>
                      <div className="chat-window-sub">
                        {selectedChat.platform} · {selectedChat.conversation_type}
                      </div>
                    </div>
                  </div>
                  <div className="header-tags">
                    <span
                      className={`tag ${
                        selectedChat.platform === "instagram"
                          ? "tag-instagram"
                          : "tag-facebook"
                      }`}
                    >
                      {selectedChat.platform}
                    </span>
                    <span
                      className={`tag ${
                        selectedChat.conversation_type === "dm"
                          ? "tag-dm"
                          : "tag-comment"
                      }`}
                    >
                      {selectedChat.conversation_type}
                    </span>
                    {selectedChat.lead_status && (
                      <span className="tag tag-status">{selectedChat.lead_status}</span>
                    )}
                  </div>
                </div>

                {/* Meta bar */}
                {(selectedChat.service_interest ||
                  selectedChat.followup_at ||
                  selectedChat.appointment_date ||
                  selectedChat.appointment_time) && (
                  <div className="meta-bar">
                    {selectedChat.service_interest && (
                      <span className="meta-bar-item">
                        <b>Service:</b> {selectedChat.service_interest}
                      </span>
                    )}
                    {selectedChat.followup_at && (
                      <span className="meta-bar-item">
                        <b>Follow-up:</b> {formatTime(selectedChat.followup_at)}
                      </span>
                    )}
                    {(selectedChat.appointment_date || selectedChat.appointment_time) && (
                      <span className="meta-bar-item">
                        <b>Appointment:</b> {selectedChat.appointment_date || ""}{" "}
                        {selectedChat.appointment_time || ""}
                      </span>
                    )}
                  </div>
                )}

                {/* Messages */}
<div
  className="messages-area"
  onScroll={(e) => {
    const el = e.target;

    const isBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight < 120;

    setShouldAutoScroll(isBottom);
  }}
>
  {messages.length === 0 && (
    <div className="msg-empty">
      <div className="msg-empty-icon">💬</div>
      <div>No messages yet</div>
    </div>
  )}

  {messages.map((m) => (
    <div
      key={m.id}
      className={`msg-bubble-wrap ${m.direction}`}
    >
      <div
        className={`msg-bubble ${m.direction}`}
      >
        <div style={{ whiteSpace: "pre-wrap" }}>
          {m.message_text}
        </div>
      </div>

      <div className="msg-meta">
        <span>{m.direction}</span>

        {m.is_bot && (
          <span className="bot-tag">🤖 Bot</span>
        )}

        {m.comment_id && (
          <span className="comment-tag">💬 Comment</span>
        )}
      </div>
    </div>
  ))}

  <div ref={messagesEndRef} />
</div>

                {/* Reply Box */}
                <div className="reply-box">
                  <input
                    className="reply-input"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") sendReply();
                    }}
                    placeholder={
                      selectedChat.conversation_type === "comment"
                        ? "Reply to comment..."
                        : "Type a DM reply..."
                    }
                  />
                  <button className="send-btn" onClick={sendReply}>
                    <span className="send-label">Send</span> ➤
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}